import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider, useApp } from './context/AppContext';
import Sidebar from './components/Sidebar';
import ProfileSlideout from './components/ProfileSlideout';
import ErrorBoundary from './components/ErrorBoundary';
import DepartmentSelector from './components/DepartmentSelector';
import './App.css';

const WorkflowDashboard = lazy(() => import('./pages/WorkflowDashboard'));
const AllWorkflows = lazy(() => import('./pages/AllWorkflows'));
const WorkflowDetail = lazy(() => import('./pages/WorkflowDetail'));
const StepDetail = lazy(() => import('./pages/StepDetail'));
const RoiDashboard = lazy(() => import('./pages/RoiDashboard'));
const FlaggedReviews = lazy(() => import('./pages/FlaggedReviews'));
const Settings = lazy(() => import('./pages/Settings'));
const WorkflowTypePage = lazy(() => import('./pages/WorkflowTypePage'));
const DeptUnassigned = lazy(() => import('./pages/DeptUnassigned'));

function LoadingFallback() {
  return <div className="route-loading">Loading...</div>;
}

function AppShell() {
  const { userProfile, profileOpen, setProfileOpen } = useApp();

  const initials =
    (userProfile.firstName?.[0] || '').toUpperCase() +
    (userProfile.lastName?.[0] || '').toUpperCase();

  return (
    <div className="app">
      <header className="top-bar">
        <div className="logo-bug">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="2.5" fill="rgba(110,255,149,1)" />
            <path d="M10 4C6.7 4 4 6.7 4 10" stroke="rgba(110,255,149,1)" strokeWidth="1.8" strokeLinecap="round" opacity="0.5" />
            <path d="M10 6C7.8 6 6 7.8 6 10" stroke="rgba(110,255,149,1)" strokeWidth="1.8" strokeLinecap="round" opacity="0.8" />
          </svg>
        </div>
        <h1 className="top-bar-title">Agent Mission Control</h1>
        <div className="top-bar-spacer" />
        <DepartmentSelector />
        <div className="top-bar-avatar">
          <button
            className="top-bar-avatar-btn"
            onClick={() => setProfileOpen(!profileOpen)}
            aria-label={profileOpen ? 'Close profile' : 'Open profile'}
          >
            {userProfile.avatarUrl ? (
              <img
                className="avatar-img"
                src={userProfile.avatarUrl}
                alt={`${userProfile.firstName} ${userProfile.lastName}`}
              />
            ) : (
              <span className="avatar-initials">{initials}</span>
            )}
          </button>
        </div>
      </header>
      <Sidebar />
      <div className="app-body">
        <main className="main-content">
          <ErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/workflows" element={<WorkflowDashboard />} />
                <Route path="/workflows/all" element={<AllWorkflows />} />
                <Route path="/workflows/type/:typeName" element={<WorkflowTypePage />} />
                <Route path="/workflows/:id" element={<WorkflowDetail />} />
                <Route path="/workflows/:id/steps/:stepId" element={<StepDetail />} />
                <Route path="/roi" element={<RoiDashboard />} />
                <Route path="/flagged" element={<FlaggedReviews />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/dept-unassigned" element={<DeptUnassigned />} />
                <Route path="*" element={<Navigate to="/workflows" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      <ProfileSlideout />
    </div>
  );
}

function AuthGate({ children }) {
  const { user, loading, login } = useAuth();

  if (loading) {
    return <div className="route-loading">Signing in...</div>;
  }

  if (!user) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <h2>Agent Mission Control</h2>
        <button onClick={login} style={{ padding: '10px 24px', fontSize: 16, cursor: 'pointer' }}>
          Sign in with Microsoft
        </button>
      </div>
    );
  }

  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthGate>
          <AppProvider>
            <AppShell />
          </AppProvider>
        </AuthGate>
      </AuthProvider>
    </BrowserRouter>
  );
}
